import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  Button,
  Alert,
  Stack,
  Tooltip,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { type ActivationTokenResponse } from '../../customer-reservations/api';
import { AccessTime, Refresh, ContentCopy, CheckCircle } from '@mui/icons-material';
import { formatTime } from '@shared/entity';
import { useCreateActivationTokenMutation } from '../../customer-reservations/queries';
import AppTheme from '@libs/mui/AppTheme';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';

type ClientActivationQRDialogProps = {
  open: boolean;
  onClose: () => void;
  clientId: string;
  clientName: string;
  clientPhone: string | null;
  clientPhonePrefix?: string | null;
  hasAllowedServices?: boolean;
};

export const ClientActivationQRDialog = ({
  open,
  onClose,
  clientId,
  clientName,
  clientPhone,
  clientPhonePrefix,
  hasAllowedServices = true,
}: ClientActivationQRDialogProps) => {
  const intl = useIntl();
  const [activationData, setActivationData] = useState<ActivationTokenResponse | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [copied, setCopied] = useState(false);
  const { mutate: generateToken, isPending: loading, error } = useCreateActivationTokenMutation();

  const generateQRCode = () => {
    generateToken(clientId, {
      onSuccess: data => {
        setActivationData(data);
        setTimeRemaining(getRemainingTime(data.expiresAt));
      },
    });
  };

  useEffect(() => {
    if (open && !activationData && hasAllowedServices) {
      generateQRCode();
    }
  }, [open, hasAllowedServices]);

  useEffect(() => {
    if (timeRemaining <= 0) return;
    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timeRemaining]);

  const handleClose = () => {
    setActivationData(null);
    setTimeRemaining(0);
    setCopied(false);
    onClose();
  };

  const handleRefresh = () => {
    setActivationData(null);
    setCopied(false);
    generateQRCode();
  };

  const handleCopyLink = async () => {
    if (!activationData?.activationUrl) return;
    try {
      await navigator.clipboard.writeText(activationData.activationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSendSMS = () => {
    if (!activationData?.activationUrl || !clientPhone) return;
    const message = intl.formatMessage(
      { id: 'customerAuth.smsActivationMessage' },
      { url: activationData.activationUrl },
    );
    const smsUrl = `sms:${clientPhonePrefix ?? ''}${clientPhone}?&body=${encodeURIComponent(message)}`;
    window.location.href = smsUrl;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {intl.formatMessage({ id: 'customerAuth.qrDialogTitle' }, { clientName })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} alignItems="center" sx={{ py: 2 }}>
          {!hasAllowedServices && (
            <Alert severity="warning" sx={{ width: '100%' }}>
              {intl.formatMessage({ id: 'clientProfilePage.qrDisabledHint' })}
            </Alert>
          )}

          {hasAllowedServices && loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {hasAllowedServices && error && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {error.message || intl.formatMessage({ id: 'customerAuth.qrDialogGenerateError' })}
            </Alert>
          )}

          {hasAllowedServices && activationData && timeRemaining > 0 && (
            <>
              <Box
                sx={{
                  p: 3,
                  bgcolor: 'white',
                  borderRadius: 2,
                  boxShadow: 2,
                }}
              >
                <QRCodeSVG value={activationData.activationUrl} size={256} level="H" />
              </Box>

              <Box sx={{ textAlign: 'center', width: '100%' }}>
                <Typography variant="body1" gutterBottom>
                  {intl.formatMessage({ id: 'customerAuth.qrDialogScanInstruction' })}
                </Typography>

                {/* Action buttons for sharing */}
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2, mb: 2 }}>
                  <Tooltip
                    title={
                      copied
                        ? intl.formatMessage({
                            id: 'customerAuth.linkCopied',
                            defaultMessage: 'Odkaz zkopírován',
                          })
                        : intl.formatMessage({
                            id: 'customerAuth.copyLink',
                            defaultMessage: 'Zkopírovat odkaz',
                          })
                    }
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                      onClick={handleCopyLink}
                      color={copied ? 'success' : 'primary'}
                    >
                      {intl.formatMessage({
                        id: 'customerAuth.copyLink',
                        defaultMessage: 'Zkopírovat odkaz',
                      })}
                    </Button>
                  </Tooltip>

                  {clientPhone && (
                    <Tooltip
                      title={intl.formatMessage({
                        id: 'customerAuth.sendViaSMS',
                        defaultMessage: 'Poslat SMS',
                      })}
                    >
                      <Button
                        size="small"
                        startIcon={
                          <SmsOutlinedIcon
                            fontSize="small"
                            sx={{
                              fill: AppTheme.palette.info.main,
                            }}
                          />
                        }
                        onClick={handleSendSMS}
                        sx={{
                          bgcolor: AppTheme.palette.info.light,
                          color: AppTheme.palette.info.main,
                          border: `1px solid  ${AppTheme.palette.info.main} `,
                          '&:hover': {
                            bgcolor: AppTheme.palette.info.light,
                            fontWeight: 'bold',
                            color: AppTheme.palette.info.main,
                          },
                        }}
                      >
                        {intl.formatMessage({
                          id: 'customerAuth.sendSMS',
                          defaultMessage: 'Poslat SMS',
                        })}
                      </Button>
                    </Tooltip>
                  )}
                </Stack>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    mt: 2,
                  }}
                >
                  <AccessTime color="action" />
                  <Typography variant="h6" color={timeRemaining < 60 ? 'error' : 'text.primary'}>
                    {formatTime(timeRemaining)}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {intl.formatMessage({ id: 'customerAuth.deviceLinkExpiresSoon' })}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleRefresh}
                disabled={loading}
              >
                {intl.formatMessage({ id: 'customerAuth.deviceLinkGenerateNew' })}
              </Button>
            </>
          )}

          {hasAllowedServices && activationData && timeRemaining <= 0 && (
            <>
              <Alert severity="warning" sx={{ width: '100%' }}>
                {intl.formatMessage({ id: 'customerAuth.qrDialogExpired' })}
              </Alert>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={handleRefresh}
                disabled={loading}
              >
                {intl.formatMessage({ id: 'customerAuth.deviceLinkGenerateNew' })}
              </Button>
            </>
          )}

          <Button onClick={handleClose} variant="text">
            {intl.formatMessage({ id: 'customerAuth.qrDialogClose' })}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const getRemainingTime = (expiresAt: string) => {
  return Math.floor(dayjs(expiresAt).diff(dayjs(), 'second'));
};
